import { useState, useEffect, useMemo } from 'react';
import { Activity, Calendar, HelpCircle, Loader2, TrendingUp } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Color scale: FIXED categorical colors (do NOT theme-link these — they're
// data encoding for the heatmap and must stay consistent across themes)
function getHeatColor(value, hasData) {
  if (!hasData || value === null) return 'rgba(255,255,255,0.03)';
  if (value >= 90) return '#22c55e';
  if (value >= 80) return '#10b981';
  if (value >= 60) return '#6366f1';
  if (value >= 40) return '#818cf8';
  if (value >= 20) return '#f59e0b';
  if (value > 0)   return '#ef4444';
  return 'rgba(100,116,139,0.15)';
}

function getHeatOpacity(value, hasData) {
  if (!hasData || value === null) return 1;
  if (value === 0) return 1;
  return 0.7 + (value / 100) * 0.3;
}

const Tooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', cursor: 'help', marginLeft: 4 }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <HelpCircle size={14} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
      {show && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          width: 280, padding: '10px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
          borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, zIndex: 100,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)', pointerEvents: 'none'
        }}>{text}</div>
      )}
    </span>
  );
};

const BaselineHeatmap = ({ cameraId, configId, recordingMode }) => {
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [learning, setLearning] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  useEffect(() => {
    if (!cameraId) return;
    setLoading(true);
    fetchWithTenant(`/api/recording-assurance/cameras/${cameraId}/baseline`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setBaseline(data.baseline);
          setLearning(data.learning);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [cameraId]);

  // Current time indicator
  const now = new Date();
  const currentDow = now.getDay();
  const currentHour = now.getHours();
  const currentSlot = currentDow * 24 + currentHour;

  // Compute stats
  const stats = useMemo(() => {
    if (!baseline) return null;
    const withData = baseline.filter(b => b.mean !== null);
    if (withData.length === 0) return null;

    const avgCoverage = Math.round(withData.reduce((s, b) => s + b.mean, 0) / withData.length);
    const peakSlot = withData.reduce((best, b) => b.mean > (best?.mean || 0) ? b : best, null);
    const lowestSlot = withData.reduce((worst, b) => (b.mean < (worst?.mean ?? 999)) ? b : worst, null);
    const quietHours = withData.filter(b => b.mean < 10).length;

    return {
      avgCoverage,
      peakSlot: peakSlot ? { day: DAYS[peakSlot.dayOfWeek], hour: peakSlot.hour, value: peakSlot.mean } : null,
      lowestSlot: lowestSlot ? { day: DAYS[lowestSlot.dayOfWeek], hour: lowestSlot.hour, value: lowestSlot.mean } : null,
      quietHours,
      dataPoints: withData.length,
      totalSlots: 168
    };
  }, [baseline]);

  if (loading) {
    return (
      <div className="ra-card" style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 size={24} className="spin" style={{ color: 'var(--text-muted)' }} />
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>Loading baseline data...</p>
      </div>
    );
  }

  if (!baseline || baseline.every(b => b.mean === null)) {
    return (
      <div className="ra-card" style={{ padding: '30px', textAlign: 'center' }}>
        <Calendar size={32} style={{ color: 'var(--text-muted)', opacity: 0.3, marginBottom: 8 }} />
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {learning?.isLearning
            ? `Baseline data is being collected (Day ${learning.dayNumber} of 7). The heatmap will populate as data arrives.`
            : 'No baseline data available for this camera yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="ra-card ra-heatmap-card">
      {/* Header */}
      <div className="ra-heatmap-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Activity size={18} />
          <h3>Weekly activity baseline</h3>
          <Tooltip text="This heatmap shows the average recording coverage for each hour of the week, based on the last 30 days of data. Bright green = high recording activity. Dark/red = low or no recording. Use this to understand your camera's normal behavior patterns and plan storage capacity." />
        </div>
        {stats && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {stats.dataPoints} of {stats.totalSlots} hours observed
          </span>
        )}
      </div>

      {/* Heatmap Grid */}
      <div className="ra-heatmap-container">
        {/* Hour labels across top */}
        <div className="ra-heatmap-grid">
          <div className="ra-heatmap-corner" />
          {HOURS.map(h => (
            <div key={h} className="ra-heatmap-hour-label">
              {h % 3 === 0 ? (h === 0 ? '12a' : h < 12 ? h + 'a' : h === 12 ? '12p' : (h - 12) + 'p') : ''}
            </div>
          ))}

          {/* Day rows */}
          {DAYS.map((day, dow) => (
            <div key={day} className="ra-heatmap-row" style={{ display: 'contents' }}>
              <div className="ra-heatmap-day-label">{day}</div>
              {HOURS.map(hour => {
                const slot = dow * 24 + hour;
                const data = baseline[slot];
                const hasData = data && data.mean !== null;
                const value = hasData ? data.mean : null;
                const isCurrent = slot === currentSlot;
                const isHovered = hoveredSlot === slot;

                return (
                  <div
                    key={hour}
                    className={`ra-heatmap-cell ${isCurrent ? 'current' : ''} ${isHovered ? 'hovered' : ''}`}
                    style={{
                      background: getHeatColor(value, hasData),
                      opacity: getHeatOpacity(value, hasData),
                    }}
                    onMouseEnter={() => setHoveredSlot(slot)}
                    onMouseLeave={() => setHoveredSlot(null)}
                  >
                    {isCurrent && <div className="ra-heatmap-current-dot" />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Hover tooltip */}
        {hoveredSlot !== null && baseline[hoveredSlot] && (
          <div className="ra-heatmap-tooltip">
            <strong>{DAYS[Math.floor(hoveredSlot / 24)]} {hoveredSlot % 24}:00 - {(hoveredSlot % 24) + 1}:00</strong>
            {baseline[hoveredSlot].mean !== null ? (
              <>
                <div>Avg coverage: <span style={{ color: getHeatColor(baseline[hoveredSlot].mean, true), fontWeight: 600 }}>{baseline[hoveredSlot].mean}%</span></div>
                <div>Variability: +/-{baseline[hoveredSlot].stdev}%</div>
                <div>Samples: {baseline[hoveredSlot].samples}</div>
              </>
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>No data yet</div>
            )}
          </div>
        )}
      </div>

      {/* Color Legend */}
      <div className="ra-heatmap-legend">
        <span className="ra-heatmap-legend-label">Less activity</span>
        <div className="ra-heatmap-legend-bar">
          <div style={{ background: 'rgba(100,116,139,0.15)', flex: 1 }} />
          <div style={{ background: '#ef4444', flex: 1 }} />
          <div style={{ background: '#f97316', flex: 1 }} />
          <div style={{ background: '#f59e0b', flex: 1 }} />
          <div style={{ background: '#0ea5e9', flex: 1 }} />
          <div style={{ background: '#00d4aa', flex: 1 }} />
          <div style={{ background: '#22c55e', flex: 1 }} />
        </div>
        <span className="ra-heatmap-legend-label">More activity</span>
      </div>

      {/* Insights */}
      {stats && (
        <div className="ra-heatmap-insights">
          <div className="ra-heatmap-insight">
            <TrendingUp size={14} />
            <span>Average coverage: <strong>{stats.avgCoverage}%</strong></span>
          </div>
          {stats.peakSlot && (
            <div className="ra-heatmap-insight">
              <Activity size={14} style={{ color: '#22c55e' }} />
              <span>Peak activity: <strong>{stats.peakSlot.day} {stats.peakSlot.hour}:00</strong> ({stats.peakSlot.value}%)</span>
            </div>
          )}
          {stats.lowestSlot && (
            <div className="ra-heatmap-insight">
              <Activity size={14} style={{ color: 'var(--text-tertiary)' }} />
              <span>Quietest: <strong>{stats.lowestSlot.day} {stats.lowestSlot.hour}:00</strong> ({stats.lowestSlot.value}%)</span>
            </div>
          )}
          {stats.quietHours > 0 && (
            <div className="ra-heatmap-insight">
              <Calendar size={14} style={{ color: 'var(--text-tertiary)' }} />
              <span><strong>{stats.quietHours}</strong> hours per week with less than 10% coverage (motion-only or inactive)</span>
            </div>
          )}
          {recordingMode === 'SCHEDULED' && (
            <div className="ra-heatmap-insight" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              <HelpCircle size={14} />
              <span>This camera has a mixed schedule. Green = continuous recording windows. Dark = motion-only windows where low coverage is expected.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BaselineHeatmap;
